import {
    ENABLE_ADDRESS_CHECK,
    ENABLE_DELIVERY_CENTER,
    ENABLE_MARKETING_PROMOTIONS,
    ENABLE_MARKETING_SEGMENTS,
    ENABLE_MARKETING_STORIES,
    ENABLE_TELEGRAM_BROADCASTS
} from "../../utils/featureFlags"
import type {PermissionKey} from "./permissions"
import type {IUserPermissionFlags} from "../user/UserApiTypes"
import {translateUi} from "../../i18n/translateUi"


export type AppIconKey =
    | "dashboard"
    | "orders"
    | "menu"
    | "reviews"
    | "marketing"
    | "address-check"
    | "delivery-center"
    | "clients"
    | "settings"
    | "team"
    | "branches"
    | "shipping"
    | "payment-types"
    | "couriers"
    | "review-categories"

export interface AppAccessRequirement {
    allOf?: readonly PermissionKey[]
    anyOf?: readonly PermissionKey[]
}

export interface AppModuleDefinition {
    name: string
    path: string
    matchPrefixes?: readonly string[]
    label: string
    icon?: AppIconKey
    parent?: string
    showInResources?: boolean
    show?: string
    enabled?: boolean
    access?: AppAccessRequirement
    children?: readonly string[]
}

const APP_MODULE_DEFINITIONS = [
    {
        name: "dashboard",
        path: "/",
        get label() { return translateUi("Главная") },
        icon: "dashboard",
        showInResources: true,
        access: {allOf: ["use_system"]}
    },
    {
        name: "orders",
        path: "/orders",
        matchPrefixes: ["/orders"],
        get label() { return translateUi("Заказы") },
        icon: "orders",
        show: "/orders/:id",
        showInResources: true,
        access: {allOf: ["read_order"]}
    },
    {
        name: "orders-queue",
        path: "/orders",
        get label() { return translateUi("Очередь") },
        parent: "orders",
        showInResources: true,
        access: {allOf: ["read_order"]}
    },
    {
        name: "orders-progress",
        path: "/orders/progress",
        get label() { return translateUi("Прогресс") },
        parent: "orders",
        showInResources: true,
        access: {allOf: ["read_order"]}
    },
    {
        name: "menu",
        path: "/menu",
        matchPrefixes: ["/menu"],
        get label() { return translateUi("Меню") },
        icon: "menu",
        showInResources: true,
        access: {allOf: ["read_menu"]}
    },
    {
        name: "reviews",
        path: "/reviews",
        matchPrefixes: ["/reviews"],
        get label() { return translateUi("Отзывы") },
        icon: "reviews",
        showInResources: true,
        access: {allOf: ["read_review"]}
    },
    {
        name: "promotions",
        path: "/promotions",
        matchPrefixes: ["/promotions"],
        get label() { return translateUi("Маркетинг") },
        icon: "marketing",
        showInResources: true,
        access: {anyOf: ["read_menu", "read_promotion"]}
    },
    {
        name: "marketing-banners",
        path: "/promotions/banners",
        matchPrefixes: ["/promotions/banners"],
        get label() { return translateUi("Баннеры") },
        access: {allOf: ["read_menu"]}
    },
    {
        name: "marketing-stories",
        path: "/promotions/stories",
        matchPrefixes: ["/promotions/stories"],
        label: "Stories",
        enabled: ENABLE_MARKETING_STORIES,
        access: {allOf: ["read_menu"]}
    },
    {
        name: "marketing-promotions",
        path: "/promotions/campaigns",
        matchPrefixes: ["/promotions/campaigns"],
        get label() { return translateUi("Акции") },
        enabled: ENABLE_MARKETING_PROMOTIONS,
        access: {allOf: ["read_promotion"]}
    },
    {
        name: "marketing-segments",
        path: "/promotions/segments",
        matchPrefixes: ["/promotions/segments"],
        get label() { return translateUi("Сегментация") },
        enabled: ENABLE_MARKETING_SEGMENTS,
        access: {allOf: ["read_promotion"]}
    },
    {
        name: "marketing-broadcasts",
        path: "/promotions/broadcasts",
        matchPrefixes: ["/promotions/broadcasts"],
        get label() { return translateUi("Telegram-рассылки") },
        enabled: ENABLE_TELEGRAM_BROADCASTS,
        access: {allOf: ["read_menu"]}
    },
    {
        name: "address-check",
        path: "/address-check",
        matchPrefixes: ["/address-check"],
        get label() { return translateUi("Проверка адреса") },
        icon: "address-check",
        showInResources: true,
        enabled: ENABLE_ADDRESS_CHECK,
        access: {allOf: ["read_menu"]}
    },
    {
        name: "delivery-center",
        path: "/delivery-center",
        matchPrefixes: ["/delivery-center"],
        get label() { return translateUi("Центр доставки") },
        icon: "delivery-center",
        showInResources: true,
        enabled: ENABLE_DELIVERY_CENTER,
        access: {allOf: ["read_order", "read_courier", "read_branch"]}
    },
    {
        name: "clients",
        path: "/clients",
        matchPrefixes: ["/clients"],
        get label() { return translateUi("Клиенты") },
        icon: "clients",
        showInResources: true,
        access: {allOf: ["read_order"]}
    },
    {
        name: "profile",
        path: "/profile",
        get label() { return translateUi("Профиль") },
        access: {allOf: ["use_system"]}
    },
    {
        name: "settings",
        path: "/settings",
        get label() { return translateUi("Настройки") },
        icon: "settings",
        showInResources: true,
        children: [
            "settings-team",
            "settings-branches",
            "settings-shipping",
            "settings-payment-types",
            "settings-couriers",
            "settings-reviews"
        ]
    },
    {
        name: "settings-team",
        path: "/settings/team",
        matchPrefixes: ["/settings/team", "/team"],
        get label() { return translateUi("Сотрудники") },
        icon: "team",
        parent: "settings",
        showInResources: true,
        access: {allOf: ["read_user"]}
    },
    {
        name: "settings-branches",
        path: "/settings/branches",
        matchPrefixes: ["/settings/branches"],
        get label() { return translateUi("Филиалы") },
        icon: "branches",
        parent: "settings",
        showInResources: true,
        access: {allOf: ["read_branch"]}
    },
    {
        name: "settings-shipping",
        path: "/settings/shipping",
        matchPrefixes: ["/settings/shipping"],
        get label() { return translateUi("Тарифы доставки") },
        icon: "shipping",
        parent: "settings",
        showInResources: true,
        access: {allOf: ["write_branch"]}
    },
    {
        name: "settings-payment-types",
        path: "/settings/payment-types",
        matchPrefixes: ["/settings/payment-types"],
        get label() { return translateUi("Типы оплаты") },
        icon: "payment-types",
        parent: "settings",
        showInResources: true,
        access: {allOf: ["write_branch"]}
    },
    {
        name: "settings-couriers",
        path: "/settings/couriers",
        matchPrefixes: ["/settings/couriers"],
        get label() { return translateUi("Курьеры") },
        icon: "couriers",
        parent: "settings",
        showInResources: true,
        access: {allOf: ["read_courier"]}
    },
    {
        name: "settings-reviews",
        path: "/settings/reviews",
        matchPrefixes: ["/settings/reviews"],
        get label() { return translateUi("Категории отзывов") },
        icon: "review-categories",
        parent: "settings",
        showInResources: true,
        access: {allOf: ["read_review"]}
    }
] as const satisfies readonly AppModuleDefinition[]

export type AppModuleName = typeof APP_MODULE_DEFINITIONS[number]["name"]

export const APP_MODULES: readonly AppModuleDefinition[] = APP_MODULE_DEFINITIONS

export const getAppModule = (name: string) => APP_MODULES.find((module) => module.name === name)

export const getAppModulePath = (name: AppModuleName) => (
    APP_MODULE_DEFINITIONS.find((module) => module.name === name)?.path || "/"
)

export const getAppModuleShowPath = (name: AppModuleName) => (
    APP_MODULES.find((module) => module.name === name)?.show || getAppModulePath(name)
)

export const getAppModuleForPath = (path: string) => {
    if (path === "/") return getAppModule("dashboard")

    return APP_MODULES
        .flatMap((module) => (module.matchPrefixes || [module.path]).map((prefix) => ({module, prefix})))
        .filter(({prefix}) => prefix !== "/" && (path === prefix || path.startsWith(`${prefix}/`)))
        .sort((first, second) => second.prefix.length - first.prefix.length)[0]?.module
}

export const isAccessRequirementSatisfied = (
    requirement: AppAccessRequirement | undefined,
    permission: IUserPermissionFlags
) => {
    const {allOf = [], anyOf = []} = requirement || {}
    return allOf.every((key) => Boolean(permission[key]))
        && (!anyOf.length || anyOf.some((key) => Boolean(permission[key])))
}

export const SETTINGS_MODULE_PATHS = APP_MODULES
    .filter((module) => module.parent === "settings")
    .map((module) => module.path)
